import { useState } from 'react';
import {
  ActionIcon,
  Anchor,
  Badge,
  Button,
  Divider,
  Group,
  NumberInput,
  Progress,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { Dropzone, MIME_TYPES } from '@mantine/dropzone';
import { IconFileUpload, IconLink, IconTrash, IconX } from '@tabler/icons-react';
import { DataTable } from 'mantine-datatable';
import { notifications } from '@mantine/notifications';
import {
  useAttachContractDocument,
  useContractDocumentsList,
  useDetachContractDocument,
} from '../../queries/contracts';
import {
  useDocumentsList,
  useFinalizeDocument,
  useRequestUploadUrl,
} from '../../queries/documents';
import { MAX_UPLOAD_SIZE_BYTES, putToR2, sha256Hex } from '../../lib/upload';
import { ApiError } from '../../lib/api-error';
import type { ContractDocument } from '../../types/api';
import { DocumentPreviewModal, type PreviewableDoc } from '../documents/DocumentPreviewModal';

/** Upload allow-list mirrors the runsheet `EntryAttachments` so /documents and
 *  /contracts share one set of accepted formats. */
const ACCEPT = [MIME_TYPES.pdf, MIME_TYPES.png, MIME_TYPES.jpeg, 'image/tiff', 'image/webp'];
const MAX_SIZE = MAX_UPLOAD_SIZE_BYTES;

interface Props {
  contractId: string;
  canWrite: boolean;
}

export function ContractDocumentsPanel({ contractId, canWrite }: Props) {
  const { data: rows = [], isLoading } = useContractDocumentsList(contractId);
  const { data: allDocs = [] } = useDocumentsList({ limit: 200 });
  const attachM = useAttachContractDocument();
  const detachM = useDetachContractDocument();

  const [selectedDoc, setSelectedDoc] = useState<string | null>(null);
  const [pageStart, setPageStart] = useState<number | ''>('');
  const [pageEnd, setPageEnd] = useState<number | ''>('');
  const [note, setNote] = useState('');
  const [previewDoc, setPreviewDoc] = useState<PreviewableDoc | null>(null);

  const attachedIds = new Set(rows.map((r) => r.document_id));
  const docOptions = allDocs
    .filter((d) => d.sha256 !== null && !attachedIds.has(d.id))
    .map((d) => ({
      value: d.id,
      label: d.original_filename ?? d.title ?? d.id.slice(0, 8),
    }));

  async function onAttach() {
    if (!selectedDoc) return;
    try {
      await attachM.mutateAsync({
        contract_id: contractId,
        document_id: selectedDoc,
        page_start: pageStart === '' ? null : Number(pageStart),
        page_end: pageEnd === '' ? null : Number(pageEnd),
        note: note.trim() || null,
      });
      setSelectedDoc(null);
      setPageStart('');
      setPageEnd('');
      setNote('');
      notifications.show({ title: 'Document attached', message: '', color: 'pine' });
    } catch (err) {
      notifications.show({
        title: 'Attach failed',
        message: err instanceof ApiError ? err.message : 'Unexpected error',
        color: 'red',
      });
    }
  }

  async function onDetach(row: ContractDocument) {
    try {
      await detachM.mutateAsync(row.id);
      notifications.show({ title: 'Document detached', message: '', color: 'pine' });
    } catch (err) {
      notifications.show({
        title: 'Detach failed',
        message: err instanceof ApiError ? err.message : 'Unexpected error',
        color: 'red',
      });
    }
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={4}>Documents</Title>
        <Badge variant="light" color="indigo">
          {rows.length} attached
        </Badge>
      </Group>

      {canWrite && (
        <Stack gap="sm">
          <Group align="flex-end" wrap="wrap">
            <Select
              label="Attach an existing document"
              description="Pick from previously uploaded, finalized documents in this project."
              placeholder="Select a document"
              data={docOptions}
              value={selectedDoc}
              onChange={setSelectedDoc}
              searchable
              clearable
              w={320}
              disabled={docOptions.length === 0}
            />
            <NumberInput
              label="Page start"
              min={1}
              value={pageStart}
              onChange={(v) => setPageStart(v === '' ? '' : Number(v))}
              w={110}
            />
            <NumberInput
              label="Page end"
              min={1}
              value={pageEnd}
              onChange={(v) => setPageEnd(v === '' ? '' : Number(v))}
              w={110}
            />
            <TextInput
              label="Note"
              value={note}
              onChange={(e) => setNote(e.currentTarget.value)}
              w={220}
            />
            <Button
              leftSection={<IconLink size={16} />}
              onClick={onAttach}
              disabled={!selectedDoc}
              loading={attachM.isPending}
            >
              Attach
            </Button>
          </Group>

          <Divider label="or upload a new file" labelPosition="center" />

          <UploadAndAttachDropzone contractId={contractId} />
        </Stack>
      )}

      <DataTable
        withTableBorder
        minHeight={120}
        fetching={isLoading}
        records={rows}
        idAccessor="id"
        noRecordsText="No documents attached yet"
        columns={[
          {
            accessor: 'original_filename',
            title: 'File',
            render: (r) => (
              <Stack gap={0}>
                <Anchor
                  component="button"
                  type="button"
                  size="sm"
                  fw={500}
                  ta="left"
                  onClick={() =>
                    setPreviewDoc({
                      id: r.document_id,
                      content_type: r.content_type ?? null,
                      original_filename: r.original_filename ?? null,
                      file_size_bytes: r.file_size_bytes ?? null,
                      sha256: r.sha256 ?? null,
                      updated_at: r.updated_at,
                    })
                  }
                >
                  {r.original_filename ?? r.document_id.slice(0, 8)}
                </Anchor>
                {r.content_type && (
                  <Text size="xs" c="dimmed">
                    {r.content_type}
                  </Text>
                )}
              </Stack>
            ),
          },
          {
            accessor: 'pages',
            title: 'Pages',
            width: 120,
            render: (r) =>
              r.page_start || r.page_end ? `${r.page_start ?? '?'}–${r.page_end ?? '?'}` : '—',
          },
          { accessor: 'note', title: 'Note', ellipsis: true },
          {
            accessor: 'actions',
            title: '',
            width: 50,
            render: (r) =>
              canWrite ? (
                <Tooltip label="Detach">
                  <ActionIcon
                    size="sm"
                    variant="subtle"
                    color="red"
                    onClick={() => onDetach(r)}
                  >
                    <IconTrash size={14} />
                  </ActionIcon>
                </Tooltip>
              ) : null,
          },
        ]}
      />

      <DocumentPreviewModal
        opened={previewDoc !== null}
        onClose={() => setPreviewDoc(null)}
        doc={previewDoc}
      />
    </Stack>
  );
}

/* ---------------------------- upload + attach ----------------------------- */

interface UploadAttachProps {
  contractId: string;
}

interface UploadStatus {
  filename: string;
  phase: 'hashing' | 'presigning' | 'uploading' | 'finalizing' | 'attaching';
  loaded?: number;
  total?: number;
}

/**
 * Drag-or-pick a new file: hash -> presign -> PUT to R2 -> finalize ->
 * attach to this contract, end-to-end, with toast notifications. Mirrors
 * UploadAndAttachDropzone in EntryAttachments.
 */
function UploadAndAttachDropzone({ contractId }: UploadAttachProps) {
  const requestUrl = useRequestUploadUrl();
  const finalize = useFinalizeDocument();
  const attach = useAttachContractDocument();
  const [status, setStatus] = useState<UploadStatus | null>(null);

  async function handleFiles(files: File[]) {
    for (const file of files) {
      try {
        setStatus({ filename: file.name, phase: 'hashing' });
        const sha = await sha256Hex(file);

        setStatus({ filename: file.name, phase: 'presigning' });
        const pres = await requestUrl.mutateAsync({
          filename: file.name,
          content_type: file.type || 'application/octet-stream',
          size_bytes: file.size,
        });

        setStatus({ filename: file.name, phase: 'uploading', loaded: 0, total: file.size });
        await putToR2({
          url: pres.upload.url,
          file,
          headers: pres.upload.headers,
          onProgress: (loaded, total) =>
            setStatus({ filename: file.name, phase: 'uploading', loaded, total }),
        });

        setStatus({ filename: file.name, phase: 'finalizing' });
        await finalize.mutateAsync({ id: pres.document.id, sha256: sha });

        setStatus({ filename: file.name, phase: 'attaching' });
        await attach.mutateAsync({
          contract_id: contractId,
          document_id: pres.document.id,
        });

        notifications.show({
          title: 'Uploaded and attached',
          message: file.name,
          color: 'pine',
        });
      } catch (err) {
        notifications.show({
          title: 'Upload failed',
          message: err instanceof ApiError || err instanceof Error ? err.message : 'error',
          color: 'red',
        });
      } finally {
        setStatus(null);
      }
    }
  }

  const phaseLabel: Record<UploadStatus['phase'], string> = {
    hashing: 'Hashing',
    presigning: 'Requesting upload URL',
    uploading: 'Uploading',
    finalizing: 'Verifying',
    attaching: 'Attaching to contract',
  };

  const uploadFraction =
    status?.phase === 'uploading' && status.total && status.total > 0
      ? Math.min(1, (status.loaded ?? 0) / status.total)
      : null;

  return (
    <Stack gap="xs">
      <Dropzone
        onDrop={handleFiles}
        accept={ACCEPT}
        maxSize={MAX_SIZE}
        loading={status !== null}
        multiple={false}
        p="md"
      >
        <Group justify="center" gap="xs" mih={64}>
          <Dropzone.Accept>
            <IconFileUpload size={28} stroke={1.5} />
          </Dropzone.Accept>
          <Dropzone.Reject>
            <IconX size={28} stroke={1.5} />
          </Dropzone.Reject>
          <Dropzone.Idle>
            <IconFileUpload size={28} stroke={1.5} />
          </Dropzone.Idle>
          <Stack gap={2}>
            <Text size="sm">
              {status
                ? `${phaseLabel[status.phase]} ${status.filename}…`
                : 'Drop a PDF or image here, or click to browse'}
            </Text>
            <Text size="xs" c="dimmed">
              PDF, JPG, PNG, TIFF, WebP — up to {Math.round(MAX_SIZE / (1024 * 1024))} MB.
              Auto-attaches to this contract on success.
            </Text>
          </Stack>
        </Group>
      </Dropzone>

      {uploadFraction !== null && (
        <Progress value={uploadFraction * 100} size="xs" striped animated />
      )}
    </Stack>
  );
}
